import React, { useEffect, useMemo, useState } from 'react';
import appointmentService from '../../services/appointmentService';
import { describeSlotError } from './SlotPicker';
import { StatusPill } from './AppointmentDetailModal';
import { formatDateLabel } from '../../utils/appointmentLogic';

/**
 * One day laid out by time slot: how full each slot is, who is booked in it, and a shortcut to book a free place.
 * Reads the same slot and list endpoints as the rest of the page, so the numbers always match.
 */
export const AppointmentDayView = ({ date, centreId, canBook, onOpen, onBookSlot, refreshKey = 0 }) => {
  const [slots, setSlots] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);

  useEffect(() => {
    if (!date) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    Promise.all([
      // A past day has no bookable slots (the server says so); the list still shows what happened that day.
      appointmentService.slots(date, centreId).catch(() => null),
      appointmentService.list({ from: date, to: date, centreId: centreId || undefined, page: 1, pageSize: 200 }),
    ])
      .then(([slotRes, listRes]) => {
        if (cancelled) return;
        setSlots(slotRes && slotRes.success ? slotRes.data : null);
        setItems(listRes.success && listRes.data ? listRes.data.items || [] : []);
      })
      .catch((err) => { if (!cancelled) setError(describeSlotError(err)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [date, centreId, refreshKey, reload]);

  const rows = useMemo(() => {
    const byStart = new Map();
    (slots?.slots || []).forEach((s) => byStart.set(s.slotStart, { slot: s, items: [] }));
    items.forEach((a) => {
      if (!byStart.has(a.slotStart)) byStart.set(a.slotStart, { slot: null, items: [], start: a.slotStart });
      byStart.get(a.slotStart).items.push(a);
    });
    return [...byStart.entries()].sort(([x], [y]) => x.localeCompare(y)).map(([start, r]) => ({ start, ...r }));
  }, [slots, items]);

  if (loading && rows.length === 0) return <div className="apt-day-grid" aria-busy="true">{Array.from({ length: 6 }).map((_, i) => <span key={i} className="apt-skeleton apt-skeleton-row" />)}</div>;
  if (error) {
    return (
      <div className="apt-state apt-state-error" role="alert">
        <p>{error}</p>
        <button type="button" className="apt-btn apt-btn-sm" onClick={() => setReload((n) => n + 1)}>Try again</button>
      </div>
    );
  }

  return (
    <div className="apt-day">
      <div className="apt-day-head">
        <strong>{formatDateLabel(date)}</strong>
        {slots?.isClosed && <span className="apt-state apt-state-closed apt-inline-state">{slots.closedReason}</span>}
        <span className="apt-muted">{items.length} appointment(s)</span>
      </div>
      {rows.length === 0 && <p className="apt-hint">No slots or appointments for this day.</p>}
      <div className="apt-day-grid">
        {rows.map((r) => {
          const cap = r.slot?.capacity || 0;
          const booked = r.slot ? cap - r.slot.available : r.items.length;
          const pct = cap > 0 ? Math.min(100, Math.round((booked / cap) * 100)) : 100;
          return (
            <div key={r.start} className={`apt-day-row ${r.slot && r.slot.available === 0 ? 'full' : ''}`}>
              <div className="apt-day-time">
                <strong>{r.start}</strong>
                {r.slot && <span className="apt-muted">{booked}/{cap}</span>}
                {r.slot && <span className="apt-meter" aria-hidden="true"><i style={{ width: `${pct}%` }} /></span>}
              </div>
              <div className="apt-day-cards">
                {r.items.map((a) => (
                  <button key={a.appointmentId} type="button" className={`apt-day-card apt-day-${a.status.toLowerCase()}`} onClick={() => onOpen(a)}>
                    <span className="apt-day-card-name">{`${a.title || ''} ${a.pName}`.trim()}</span>
                    <span className="apt-day-card-meta">
                      {a.tokenNo ? <span className="apt-token">#{a.tokenNo}</span> : null}
                      <StatusPill status={a.status} />
                    </span>
                  </button>
                ))}
                {r.slot && r.slot.isBookable && canBook && (
                  <button type="button" className="apt-day-add" onClick={() => onBookSlot(date, r.start)} aria-label={`Book ${r.start}`}>
                    + Book ({r.slot.available} free)
                  </button>
                )}
                {r.items.length === 0 && !(r.slot && r.slot.isBookable && canBook) && <span className="apt-muted apt-day-empty">{r.slot?.isPast ? 'past' : 'no bookings'}</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AppointmentDayView;
